import { z } from "zod";
import { lazySchema } from "@repo/lazy-schema";
import {
  AssetTradeAssetIdSchema,
  AssetTradeCanonicalAssetIdSchema,
  AssetTradeCashUsdAmountSchema,
  AssetTradeCreateRequestSchema,
  AssetTradeMaxRequestSchema,
  AssetTradePreviewRequestSchema,
  AssetTradePreviewResponseSchema,
} from "./asset-trades.js";
import {
  ExecutionCorrelationIdSchema,
  ExecutionPortfolioIdSchema,
  ExecutionRequestKeySchema,
  ExpectedMarketOrderVenueSchema,
  MarketOrderAccountIdSchema,
  MAX_EXECUTION_OPERATION_ACTIONS,
  NonNegativeIntegerAmountSchema,
  PositiveIntegerAmountSchema,
} from "./requests.js";
import {
  ExecutionOperationResponseSchema,
  LiveDescriptorSchema,
} from "./responses.js";

/** Decimal asset units (shares for equities), never a chain's atomic units. */
const quantity = lazySchema(() =>
  z.string().regex(/^(?:0|[1-9]\d{0,49})(?:\.\d{1,36})?$/u),
);
const positiveQuantity = lazySchema(() =>
  quantity.refine((value) => /[1-9]/u.test(value), "quantity must be positive"),
);

export const CanonicalSellAmountSchema = lazySchema(() =>
  z.union([
    z.object({ all: z.literal(true) }).strict(),
    z.object({ quantity: positiveQuantity }).strict(),
    z.object({ usd: AssetTradeCashUsdAmountSchema }).strict(),
  ]),
);

export const CanonicalSellMaxRequestSchema = lazySchema(() =>
  z
    .object({
      portfolioId: ExecutionPortfolioIdSchema,
      assetCanonicalId: AssetTradeCanonicalAssetIdSchema,
      side: z.literal("sell"),
    })
    .strict(),
);

export const CanonicalSellPreviewRequestSchema = lazySchema(() =>
  CanonicalSellMaxRequestSchema.extend({
    requestKey: ExecutionRequestKeySchema,
    correlationId: ExecutionCorrelationIdSchema.optional(),
    amount: CanonicalSellAmountSchema,
  }),
);

/**
 * One held deployment of the canonical asset. `accountRef` is the vault it
 * sells from: an EVM vault (lowercased) or the Investing Account's Solana
 * vault (base58 verbatim), so one canonical sale spans both VMs.
 */
export const CanonicalSellPositionSchema = lazySchema(() =>
  z
    .object({
      assetId: AssetTradeAssetIdSchema,
      accountRef: MarketOrderAccountIdSchema,
      chainId: z.number().int().positive(),
      decimals: z.number().int().min(0).max(36),
      symbol: z.string().min(1),
      /** 1 for direct crypto; the effective recorded factor for tokenized shares. */
      unitsPerToken: positiveQuantity,
      availableRaw: NonNegativeIntegerAmountSchema,
      heldRaw: NonNegativeIntegerAmountSchema,
      quantity,
    })
    .strict(),
);

export const CanonicalSellMaxResponseSchema = lazySchema(() =>
  CanonicalSellMaxRequestSchema.extend({
    symbol: z.string().min(1),
    amount: z.object({ quantity }).strict(),
    positions: z.array(CanonicalSellPositionSchema).max(64),
    excluded: z
      .array(
        z
          .object({
            assetId: AssetTradeAssetIdSchema,
            reason: z.enum([
              "representation_unsupported",
              "chain_unsupported",
              "no_sell_route",
              "account_unavailable",
            ]),
          })
          .strict(),
      )
      .max(256),
    reason: z
      .enum(["nothing_to_sell", "fully_reserved", "not_tradable"])
      .nullable(),
    capturedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
  }).superRefine((max, ctx) => {
    if ((max.amount.quantity === "0") !== (max.reason !== null)) {
      ctx.addIssue({
        code: "custom",
        message: "reason is present exactly when Max is zero",
      });
    }
    if (Date.parse(max.expiresAt) <= Date.parse(max.capturedAt)) {
      ctx.addIssue({
        code: "custom",
        message: "expiresAt must follow capturedAt",
      });
    }
  }),
);

const retainedLeg = lazySchema(() =>
  z
    .object({
      assetId: AssetTradeAssetIdSchema,
      accountRef: MarketOrderAccountIdSchema,
      amountRaw: PositiveIntegerAmountSchema,
      quantity: positiveQuantity,
      reason: z.literal("below_minimum"),
    })
    .strict(),
);

/** Engine-signed selections. Create revalidates them; the browser never allocates. */
export const CanonicalSellPlanBasisSchema = lazySchema(() =>
  z
    .object({
      version: z.literal(1),
      expiresAt: z.number().int().nonnegative(),
      legs: z
        .array(
          z
            .object({
              assetId: AssetTradeAssetIdSchema,
              accountRef: MarketOrderAccountIdSchema,
              decimals: z.number().int().min(0).max(36),
              unitsPerToken: positiveQuantity,
              amountRaw: PositiveIntegerAmountSchema,
              venue: ExpectedMarketOrderVenueSchema,
            })
            .strict(),
        )
        .min(1)
        .max(MAX_EXECUTION_OPERATION_ACTIONS),
      retained: z.array(retainedLeg).max(64),
      integrity: z.string().regex(/^acs1\.[a-f0-9]{64}$/u),
    })
    .strict(),
);

export const CanonicalSellCreateRequestSchema = lazySchema(() =>
  CanonicalSellPreviewRequestSchema.extend({
    planBasis: CanonicalSellPlanBasisSchema,
  }),
);

export const CanonicalSellPreviewResponseSchema = lazySchema(() =>
  z
    .object({
      flow: z.literal("canonical_asset_sell"),
      portfolioId: ExecutionPortfolioIdSchema,
      assetCanonicalId: AssetTradeCanonicalAssetIdSchema,
      side: z.literal("sell"),
      availability: CanonicalSellMaxResponseSchema,
      quantity,
      legs: z
        .array(
          z
            .object({
              amountRaw: PositiveIntegerAmountSchema,
              quantity: positiveQuantity,
              preview: AssetTradePreviewResponseSchema,
            })
            .strict(),
        )
        .max(64),
      retained: z.array(retainedLeg).max(64),
      canSubmit: z.boolean(),
      expiresAt: z.number().int().nonnegative(),
      planBasis: CanonicalSellPlanBasisSchema.nullable(),
    })
    .strict()
    .superRefine((preview, ctx) => {
      if (preview.canSubmit !== (preview.planBasis !== null)) {
        ctx.addIssue({
          code: "custom",
          message: "planBasis is present exactly when canSubmit",
        });
      }
    }),
);

export const CanonicalSellOperationResponseSchema = lazySchema(() =>
  ExecutionOperationResponseSchema.extend({
    flow: z.literal("canonical_asset_sell"),
    side: z.literal("sell"),
    live: LiveDescriptorSchema,
    portfolioLive: LiveDescriptorSchema,
  }),
);

export type CanonicalSellAmount = z.infer<typeof CanonicalSellAmountSchema>;
export type CanonicalSellMaxRequest = z.infer<
  typeof CanonicalSellMaxRequestSchema
>;
export type CanonicalSellMaxResponse = z.infer<
  typeof CanonicalSellMaxResponseSchema
>;
export type CanonicalSellPosition = z.infer<typeof CanonicalSellPositionSchema>;
export type CanonicalSellPreviewRequest = z.infer<
  typeof CanonicalSellPreviewRequestSchema
>;
export type CanonicalSellPreviewResponse = z.infer<
  typeof CanonicalSellPreviewResponseSchema
>;
export type CanonicalSellPlanBasis = z.infer<
  typeof CanonicalSellPlanBasisSchema
>;
export type CanonicalSellCreateRequest = z.infer<
  typeof CanonicalSellCreateRequestSchema
>;
export type CanonicalSellOperationResponse = z.infer<
  typeof CanonicalSellOperationResponseSchema
>;

/** One HTTP front door, preserving concrete trades and canonical buys unchanged. */
export const TradePreviewRequestSchema = lazySchema(() =>
  z.union([AssetTradePreviewRequestSchema, CanonicalSellPreviewRequestSchema]),
);
export const TradeCreateRequestSchema = lazySchema(() =>
  z.union([AssetTradeCreateRequestSchema, CanonicalSellCreateRequestSchema]),
);
export const TradeMaxRequestSchema = lazySchema(() =>
  z.union([AssetTradeMaxRequestSchema, CanonicalSellMaxRequestSchema]),
);

/** Public, immutable admission amounts; these are not quoted or inferred fills. */
export const CanonicalSellAdmittedLegSchema = lazySchema(() =>
  z.object({
    assetId: AssetTradeAssetIdSchema,
    accountRef: MarketOrderAccountIdSchema,
    chainId: z.number().int().positive(),
    symbol: z.string().min(1),
    decimals: z.number().int().min(0).max(36),
    unitsPerToken: positiveQuantity,
    amountRaw: PositiveIntegerAmountSchema,
    quantity: positiveQuantity,
    actionIndices: z
      .array(
        z
          .number()
          .int()
          .min(0)
          .max(MAX_EXECUTION_OPERATION_ACTIONS - 1),
      )
      .min(1),
  }),
);

export const CanonicalSellAdmittedIntentSchema = lazySchema(() =>
  z.object({
    kind: z.literal("canonical_asset_sell"),
    portfolioId: ExecutionPortfolioIdSchema,
    assetCanonicalId: AssetTradeCanonicalAssetIdSchema,
    side: z.literal("sell"),
    amount: CanonicalSellAmountSchema,
    symbol: z.string().min(1),
    legs: z
      .array(CanonicalSellAdmittedLegSchema)
      .min(1)
      .max(MAX_EXECUTION_OPERATION_ACTIONS),
    retained: z.array(retainedLeg).max(64),
  }),
);

export const CanonicalSellProgressSchema = lazySchema(() =>
  z
    .object({
      operationId: z.string().min(1),
      portfolioId: ExecutionPortfolioIdSchema,
      assetCanonicalId: AssetTradeCanonicalAssetIdSchema,
      symbol: z.string().min(1),
      retained: z.array(retainedLeg).max(64),
      settlementPending: z.boolean(),
      state: z.enum([
        "accepted",
        "running",
        "retrying",
        "awaiting_user",
        "completed",
        "failed",
        "cancelled",
      ]),
      legs: z.array(
        CanonicalSellAdmittedLegSchema.extend({
          status: z.enum([
            "pending",
            "completed",
            "settlement_delayed",
            "failed",
            "not_started",
          ]),
        }),
      ),
    })
    .strict(),
);
export type CanonicalSellProgress = z.infer<typeof CanonicalSellProgressSchema>;
